<script lang="ts">
	import { type Snippet } from 'svelte'
	import { fade, fly } from 'svelte/transition'
	import { goto } from '$app/navigation'
	import { page } from '$app/state'
	import { appState } from '$lib/app-state.svelte'
	import SectionHeading from '$lib/public/SectionHeading.svelte'

	interface Props {
		id: string
		title: string
		focusField?: HTMLInputElement
		children?: Snippet
	}

	let { id, title, focusField, children }: Props = $props()
	const titleId = $state(`${id}Title`)
	let isVisible = $state(false)

	$effect(() => {
		if (page.url.hash === `#${id}`) {
			open()
		} else {
			isVisible = false
		}
	})

	const open = async () => {
		isVisible = true
		document.body.style.overflow = 'hidden' // Prevent scrolling
		appState.formInvalid = false // If user hit back button from offcanvas ensure starting state
		setTimeout(() => focusField?.focus(), 300) // wait for transition
	}

	const back = () => {
		if (document.referrer.startsWith(page.url.origin)) {
			history.back() // needed to clear hash from URL
		} else {
			const url = page.url.href.replace(page.url.hash, '')
			goto(url, { replaceState: false })
		}
	}

	export const close = () => {
		isVisible = false
		appState.formInvalid = false
		document.body.style.overflow = '' // Restore scrolling
		back()
	}

	const onkeydown = (event: KeyboardEvent) => {
		if (isVisible && page.url.hash === `#${id}` && event.key === 'Escape') {
			event.stopPropagation()
			close()
		}
	}

	const onbackdropclick = (event: MouseEvent) => {
		event.stopPropagation()
		close()
	}
</script>

<svelte:window {onkeydown} />

{#if isVisible}
	<div
		onclick={onbackdropclick}
		transition:fade={{ duration: 150 }}
		class="tw:fixed tw:inset-0 tw:bg-black/50 tw:z-1040"
		role="presentation">
	</div>

	<div
		{id}
		class="tw:translate-x-0 tw:z-1045 tw:fixed tw:top-0 tw:bottom-0 tw:right-0 tw:w-100 tw:max-w-full tw:bg-white tw:border-l tw:border-gray-200 tw:shadow-lg"
		transition:fly={{ x: 400, duration: 300 }}
		tabindex="-1"
		aria-labelledby={titleId}
		aria-hidden={!isVisible}
		aria-modal={isVisible}
		role="dialog">
		<div class="tw:flex tw:items-center tw:justify-between tw:p-4">
			<SectionHeading id={titleId}>{title}</SectionHeading>
			<button
				onclick={close}
				type="button"
				class="tw:p-0 tw:border-0 tw:mb-1 tw:bg-transparent tw:text-plum tw:hover:text-amber-500"
				aria-label="Close">
				<svg height="24" width="24">
					<title>Close offcanvas</title>
					<use href="/images/icons/icons.svg#x" />
				</svg>
			</button>
		</div>
		<div class="tw:p-4">
			{@render children?.()}
		</div>
	</div>
{/if}
